import { create } from 'zustand';
import { DEFAULT_HEALTH_PALETTE } from '../domains/genogram/constants/defaultHealthPalette';
import { SessionMetrics } from '../domains/analytics/services/SessionMetrics';

export type Perspective =
  | 'general'
  | 'cultural'
  | 'health'
  | 'relationship_dynamics'
  | 'risk_services'
  | 'legal_estate'
  | 'research_quality'
  | 'timeline_events';

export interface CulturePalette {
  [tag: string]: string; // color
}

export interface HealthPaletteItem {
  color: string;
  icon?: string; // emoji or icon id
  propagate?: boolean;
}

export interface HealthPalette {
  [tag: string]: HealthPaletteItem;
}

export type HealthViewMode = 'ring' | 'ring+emoji';

interface PerspectiveState {
  perspective: Perspective;
  culturePalette: CulturePalette;
  healthPalette: HealthPalette;
  healthViewMode: HealthViewMode;
  healthFocusCondition: string | null;
  healthFocusIndexPersonId: string | null;
  showRelationshipCaptions: boolean;
  setPerspective: (p: Perspective) => void;
  setCultureColor: (tag: string, color: string) => void;
  setHealthItem: (tag: string, item: Partial<HealthPaletteItem>) => void;
  removeCultureTag: (tag: string) => void;
  removeHealthTag: (tag: string) => void;
  setHealthViewMode: (mode: HealthViewMode) => void;
  setHealthFocus: (condition: string | null, indexPersonId?: string | null) => void;
  clearHealthFocus: () => void;
  setShowRelationshipCaptions: (show: boolean) => void;
}

const defaultCulture: CulturePalette = {
  family_origin: '#84cc16',
  immigrant_origin: '#3b82f6',
  local_culture: '#a855f7',
  caucasian: '#f59e0b',
  scandinavian: '#3b82f6',
  asian: '#10b981',
  korean: '#10b981',
  hispanic: '#f97316',
  african_american: '#8b5cf6',
};

const defaultHealth: HealthPalette = { ...DEFAULT_HEALTH_PALETTE };

export const usePerspectiveStore = create<PerspectiveState>((set) => ({
  perspective: 'general',
  culturePalette: defaultCulture,
  healthPalette: defaultHealth,
  healthViewMode: 'ring',
  healthFocusCondition: null,
  healthFocusIndexPersonId: null,
  showRelationshipCaptions: true,
  setPerspective: (p) => {
    SessionMetrics.increment('genogram.viewpoint.changed');
    set({ perspective: p });
  },
  setCultureColor: (tag, color) => set((s) => ({ culturePalette: { ...s.culturePalette, [tag]: color } })),
  setHealthItem: (tag, item) => set((s) => ({ healthPalette: { ...s.healthPalette, [tag]: { ...(s.healthPalette[tag] || { color: '#999' }), ...item } } })),
  removeCultureTag: (tag) => set((s) => { const next = { ...s.culturePalette }; delete next[tag]; return { culturePalette: next }; }),
  removeHealthTag: (tag) => set((s) => { const next = { ...s.healthPalette }; delete next[tag]; return { healthPalette: next } as any; }),
  setHealthViewMode: (mode) => set({ healthViewMode: mode }),
  setHealthFocus: (condition, indexPersonId = null) =>
    set({ healthFocusCondition: condition, healthFocusIndexPersonId: indexPersonId }),
  clearHealthFocus: () => set({ healthFocusCondition: null, healthFocusIndexPersonId: null }),
  setShowRelationshipCaptions: (show) => set({ showRelationshipCaptions: show }),
}));
